import { getDCValidationLogsWithConditionsSchema } from './../../types/schemas/dataCollection/getDCValidationLogsWithConditions';
import { axios } from './axiosClient';
import { validate } from '@/utils/errors/helpers';
import { getDCMeasurementUnitsResponseSchema } from '@/types/schemas/dataCollection/getDCMeasurementUnits';
import { getDCPaymentTypesResponseSchema } from '@/types/schemas/dataCollection/getDCPaymentTypes';
import { getDCMaterialAlterCodesResponseSchema } from '@/types/schemas/dataCollection/getDCMaterialAlterCodes';
import { getDCDocumentTypesResponseSchema } from '@/types/schemas/dataCollection/getDCDocumentTypes';
import { getDCCustomersResponseSchema } from '@/types/schemas/dataCollection/getDCCustomers';
import { getDCWarehousesResponseSchema } from '@/types/schemas/dataCollection/getDCWarehouses';
import type {
  DCCustomer,
  DCDocumentHeader,
  DCDocumentType,
  DCMaterialAlterCode,
  DCMeasurementUnit,
  DCPaymentType,
  DCWarehouse,
  DCRepresentativeImport,
  DCTraderSite,
  DCValidationLog,
  DCValidationLogsFiltersData,
  RepresentativeFiltersData,
  TransactionsFiltersData,
} from '@/types/DataCollection';
import type { DataCollectionEntity } from '@/types/RecordMapping';
import { getDCDocumentHeadersWithConditionsSchema } from '@/types/schemas/dataCollection/getDCDocumentHeadersWithConditions';
import { getDCDocumentHeaderByIdSchema } from '@/types/schemas/dataCollection/getDcDocumentHeaderById';
import { getDCTraderSitesResponseSchema } from '@/types/schemas/dataCollection/getDCTraderSites';
import { parseDateManually } from '@/utils/date';

export async function getDCMeasurementUnitsWithConditions({
  page = 1,
  itemsPerPage = 0,
  query = '',
  sortColumn,
  sortAscending,
  isActive,
  representativeId = null,
  isMapped,
}: RepresentativeFiltersData): Promise<{ data: DCMeasurementUnit[]; count: number }> {
  const skip = (page - 1) * itemsPerPage;

  const url = `v2/datacollection/measurement-unit/search?take=${itemsPerPage}&skip=${skip}`;

  const response = await axios.post(url, {
    searchText: query,
    sortColumn,
    sortAscending,
    isActive,
    representativeId,
    isMapped,
  });

  const valid = validate(getDCMeasurementUnitsResponseSchema, response.data);
  return { data: valid.data as DCMeasurementUnit[], count: response.data.count };
}

export async function addOrEditDCMeasurementUnit(measurementUnits: DCMeasurementUnit[]) {
  const response: any = await axios.post('v2/datacollection/measurement-unit', measurementUnits);
  if (response.status === 201) return true;
  return false;
}

export async function removeDCMeasurementUnitsByIds(ids: string[]): Promise<string | null> {
  const response = await axios.post('v2/datacollection/measurement-unit/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });

  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getDCPaymentTypesWithCondition({
  page = 1,
  itemsPerPage = 0,
  query = '',
  sortColumn,
  sortAscending,
  isActive,
  representativeId = null,
  isMapped,
}: RepresentativeFiltersData): Promise<{ data: DCPaymentType[]; count: number }> {
  const skip = (page - 1) * itemsPerPage;

  const url = `v2/datacollection/payment-type/search?take=${itemsPerPage}&skip=${skip}`;

  const response = await axios.post(url, {
    searchText: query,
    sortColumn,
    sortAscending,
    isActive,
    representativeId,
    isMapped,
  });

  const valid = validate(getDCPaymentTypesResponseSchema, response.data);
  return { data: valid.data as DCPaymentType[], count: response.data.count };
}

export async function addOrEditDCPaymentType(paymentTypes: DCPaymentType[]) {
  const response: any = await axios.post('v2/datacollection/payment-type', paymentTypes);
  if (response.status === 201) return true;
  return false;
}

export async function removeDCPaymentTypesByIds(ids: string[]): Promise<string | null> {
  const response = await axios.post('v2/datacollection/payment-type/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });

  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getDCWarehousesWithConditions({
  page = 1,
  itemsPerPage = 0,
  query = '',
  sortColumn,
  sortAscending,
  isActive,
  representativeId = null,
  isMapped,
}: RepresentativeFiltersData): Promise<{ data: DCWarehouse[]; count: number }> {
  const skip = (page - 1) * itemsPerPage;

  const url = `v2/datacollection/warehouse/search?take=${itemsPerPage}&skip=${skip}`;

  const response = await axios.post(url, {
    searchText: query,
    sortColumn,
    sortAscending,
    isActive,
    representativeId,
    isMapped,
  });

  const valid = validate(getDCWarehousesResponseSchema, response.data);
  return { data: valid.data as DCWarehouse[], count: valid.count };
}

export async function addOrEditDCWarehouse(warehouses: DCWarehouse[]) {
  const response = await axios.post('v2/datacollection/warehouse', warehouses);
  if (response.status === 201) return true;
  return false;
}

export async function getDCMaterialAlterCodesWithConditions({
  page = 1,
  itemsPerPage = 0,
  query = '',
  sortColumn,
  sortAscending,
  isActive,
  representativeId = null,
  isMapped,
}: RepresentativeFiltersData): Promise<{
  data: DCMaterialAlterCode[];
  count: number;
}> {
  const skip = (page - 1) * itemsPerPage;

  const url = `/v2/datacollection/material-alter-code/search?take=${itemsPerPage}&skip=${skip}`;

  const response = await axios.post(url, {
    searchText: query,
    sortColumn,
    sortAscending,
    isActive,
    representativeId,
    isMapped,
  });

  const valid = validate(getDCMaterialAlterCodesResponseSchema, response.data);
  return { data: valid.data as DCMaterialAlterCode[], count: response.data.count };
}

export async function addOrEditDCMaterialAlterCode(materialAlterCodes: DCMaterialAlterCode[]) {
  const response: any = await axios.post(
    'v2/datacollection/material-alter-code',
    materialAlterCodes
  );
  if (response.status === 201) return true;
  return false;
}

export async function removeDCMaterialAlterCodesByIds(ids: string[]): Promise<string | null> {
  const response = await axios.post('v2/datacollection/material-alter-code/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });

  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getDCDocumentTypesWithConditions({
  page = 1,
  itemsPerPage = 0,
  query = '',
  sortColumn,
  sortAscending,
  isActive,
  isMapped,
  representativeId,
}: RepresentativeFiltersData): Promise<{
  data: DCDocumentType[];
  count: number;
}> {
  const skip = (page - 1) * itemsPerPage;

  const url = `/v2/datacollection/document-type/search?take=${itemsPerPage}&skip=${skip}`;

  const response = await axios.post(url, {
    searchText: query,
    sortColumn,
    sortAscending,
    isActive,
    isMapped,
    representativeId,
  });

  const valid = validate(getDCDocumentTypesResponseSchema, response.data);
  return { data: valid.data as DCDocumentType[], count: valid.count };
}

export async function addOrEditDCDocumentType(documentTypes: DCDocumentType[]) {
  const response: any = await axios.post('v2/datacollection/document-type', documentTypes);
  if (response.status === 201) return true;
  return false;
}

export async function getDCDocumentHeadersWithConditions({
  page = 1,
  itemsPerPage = 0,
  query = '',
  sortColumn,
  sortAscending,
  isInvalid,
  toApprove,
  representativesIds,
  isActive,
  exported,
  dateFrom,
  dateTo,
}: TransactionsFiltersData): Promise<{ data: Partial<DCDocumentHeader>[]; count: number }> {
  const skip = (page - 1) * itemsPerPage;
  const response = await axios.post(
    `/v2/datacollection/document-header/search?take=${itemsPerPage}&skip=${skip}`,
    {
      searchText: query,
      sortColumn,
      sortAscending,
      isInvalid,
      toApprove,
      representativesIds,
      isActive,
      exported,
      dateFrom: dateFrom || null,
      dateTo: dateTo || null,
    }
  );

  const valid = validate(getDCDocumentHeadersWithConditionsSchema, response.data.data);
  return { data: valid, count: response.data.count };
}

export async function getDCDocumentHeaderById(
  id: string
): Promise<Partial<DCDocumentHeader> | null> {
  const response = await axios.get(`/v2/datacollection/document-header/${id}`, {
    validateStatus: (status: number) => status == 404 || status == 200,
  });

  if (response.status === 404) return null;

  return validate(getDCDocumentHeaderByIdSchema, response.data);
}

export async function getDCRepresentativeImports(
  representativeId: string
): Promise<DCRepresentativeImport[] | null> {
  const response = await axios.get(
    `/v2/datacollection/representative-imports/${representativeId}`,
    {
      validateStatus: (status: number) => status == 404 || status == 200,
    }
  );

  if (response.status === 404) return null;

  return response.data.data as DCRepresentativeImport[];
}

export async function getDataCollectionEntities(): Promise<DataCollectionEntity[]> {
  const response = await axios.get('v2/datacollection/target-entities');
  return response.data as DataCollectionEntity[];
}

export async function addOrEditDCRepresentativeImport(
  representativeImports: DCRepresentativeImport[]
) {
  const response: any = await axios.post(
    'v2/datacollection/representative-imports',
    representativeImports
  );
  if (response.status === 201) return true;
  return false;
}

export async function getDCCustomersWithConditions({
  page = 1,
  itemsPerPage = 0,
  query = '',
  sortColumn,
  sortAscending,
  isActive,
  representativeId,
  isMapped,
}: RepresentativeFiltersData): Promise<{ data: DCCustomer[]; count: number }> {
  const skip = (page - 1) * itemsPerPage;
  const url = `/v2/datacollection/customers/search?take=${itemsPerPage}&skip=${skip}`;

  const response = await axios.post(url, {
    searchText: query,
    sortColumn,
    sortAscending,
    isActive,
    representativeId,
    isMapped,
  });

  const valid = validate(getDCCustomersResponseSchema, response.data);
  return { data: valid.data as DCCustomer[], count: valid.count };
}

export async function getDCDocumentTypes(
  representativeId?: string
): Promise<DCDocumentType[] | null> {
  const response = await axios.get(`v2/datacollection/document-type/${representativeId}`, {
    validateStatus: (status: number) => status == 404 || status == 200,
  });

  if (response.status === 404) return null;

  const documentTypesResponse = validate(getDCDocumentTypesResponseSchema, response.data);
  return documentTypesResponse.data as DCDocumentType[];
}

export async function addOrEditDCCustomer(customers: DCCustomer[]) {
  const response: any = await axios.post('v2/datacollection/customer', customers, {
    validateStatus: (status: number) => status === 400 || status === 201,
  });
  return response.data;
}

export async function addOrUpdateDCDocumentHeader(
  documentHeader: Partial<DCDocumentHeader>
): Promise<Partial<DCDocumentHeader> | null> {
  const response = await axios.post('v2/datacollection/document-header', documentHeader);
  if (response.status === 201) return response.data.value;
  return null;
}

export async function getDocumentFieldRequirements(
  representativeId: string
): Promise<{ header: Record<string, boolean>; details: Record<string, boolean> } | null> {
  const response = await axios.get(
    `v2/datacollection/document-field-requirements/${representativeId}`
  );
  if (response.status === 200) {
    return response.data;
  }
  return null;
}

export async function getDCTraderSitesWithConditions({
  page = 1,
  itemsPerPage = 0,
  query = '',
  sortColumn,
  sortAscending,
  isActive,
  representativeId,
  traderId,
  isMapped,
}: RepresentativeFiltersData): Promise<{
  data: DCTraderSite[];
  count: number;
}> {
  const skip = (page - 1) * itemsPerPage;

  const url = `/v2/datacollection/representatives/trader-sites/search?take=${itemsPerPage}&skip=${skip}`;

  const response = await axios.post(url, {
    searchText: query,
    sortColumn,
    sortAscending,
    isActive,
    representativeId,
    traderId,
    isMapped,
  });

  const valid = validate(getDCTraderSitesResponseSchema, response.data);
  return { data: valid.data as DCTraderSite[], count: valid.count };
}

export async function addOrEditDCTraderSite(traderSites: DCTraderSite[]) {
  const response: any = await axios.post(
    'v2/datacollection/representatives/trader-sites',
    traderSites
  );
  if (response.status === 201) return true;
  return false;
}

export async function getDCValidationLogsWithConditions({
  page = 1,
  itemsPerPage = 0,
  query = '',
  sortColumn,
  sortAscending,
  isActive,
  representativeIds,
  tableNames,
  exported,
  dateFrom,
  dateTo,
}: DCValidationLogsFiltersData): Promise<{ data: DCValidationLog[]; count: number }> {
  const skip = (page - 1) * itemsPerPage;

  const url = `/v2/datacollection/validation-logs/search?take=${itemsPerPage}&skip=${skip}`;

  const response = await axios.post(url, {
    searchText: query,
    sortColumn,
    sortAscending,
    isActive,
    representativeIds,
    tableNames,
    exported,
    dateFrom: dateFrom ? parseDateManually(dateFrom) : null,
    dateTo: dateTo ? parseDateManually(dateTo) : null,
  });

  const valid = validate(getDCValidationLogsWithConditionsSchema, response.data.data);
  return { data: valid as DCValidationLog[], count: response.data.count };
}

export async function getValidationLogsDistinctTableNames(): Promise<string[]> {
  const response = await axios.get('/v2/datacollection/validation-logs/table-names');

  return response.data.data as string[];
}
